import { PlaeePositionResponseDtoSide } from '@/lib/models';
import type { EventDetailMarketDto, PlaeePositionResponseDto } from '@/lib/models';
import { resolvePositionLeg, type ResolvedPositionLeg } from '@/lib/trading/position';

type PositionRef = Pick<PlaeePositionResponseDto, 'outcomeId' | 'side'>;

/** Answer label of a resolved option leg, e.g. "JD Vance" or "Yes". */
export function legAnswerLabel(leg: ResolvedPositionLeg): string {
  return leg.option.label ?? (leg.betSide === 'yes' ? 'Yes' : 'No');
}

/**
 * Builds a position → answer-label resolver for one event, e.g. "JD Vance"
 * or "Yes". Prebuilds the lookup context once — call it per row.
 *
 * Live markets resolve exactly through the tradeable options (the netted
 * side decides Yes/No on shared-outcome legs). A closed market has no
 * options, so the label falls back to the settlement `results` row keyed by
 * the position's **external** outcome id: its candidate name when it has
 * one, else its Yes/No identity from the outcome's `side` — flipped for a
 * netted SHORT, which holds the opposite contract. That reproduces the live
 * rule for every market shape, including the distinct unnamed No-leg of a
 * 2-outcome binary.
 */
export function positionLabeler(
  markets: Pick<EventDetailMarketDto, 'options' | 'results'>[],
): (position: PositionRef) => string {
  const options = markets.flatMap((m) => m.options);
  const resultByExternalId = new Map(markets.flatMap((m) => m.results).map((r) => [r.externalOutcomeId, r]));

  return (position) => {
    const leg = resolvePositionLeg(position.outcomeId, position.side, options);
    if (leg) return legAnswerLabel(leg);

    const long = position.side === PlaeePositionResponseDtoSide.LONG;
    const result = resultByExternalId.get(position.outcomeId);
    if (result?.label) return result.label;

    const baseIsYes = result ? result.side?.toUpperCase() !== 'NO' : true;
    return baseIsYes === long ? 'Yes' : 'No';
  };
}
